<template>
  <page :name="$t('Conto Artera')" :state="accountState">
    <pf-page-section>
      <pf-grid gutter>
        <pf-card v-if="login.sconto > 0" :class="[gridStyles.gridItem, gridStyles.modifiers['4ColOnSm'], gridStyles.modifiers['3ColOnXl']]">
          <pf-card-header>
            <pf-card-header-main> <WalletIcon /> {{ $t('Saldo provvigioni') }} </pf-card-header-main>
          </pf-card-header>
          <pf-divider />

          <pf-card-body>
            <pf-title h="1">{{ $currency(totalProvvigioni, login.currency) }}</pf-title>
          </pf-card-body>
          <pf-card-footer>
            <div>
              {{ $t('Sconto / Commissione') }}: <strong>{{ $percent(login.sconto / 100) }}</strong>
            </div>
            <div>
              {{ $t('Commissione segnalazione') }}: <strong>{{ $percent(account.referral_percentage / 100) }}</strong>
            </div>
          </pf-card-footer>
        </pf-card>

        <pf-card :class="[gridStyles.gridItem, gridStyles.modifiers['4ColOnSm'], gridStyles.modifiers['3ColOnXl']]">
          <pf-card-header>
            <pf-card-header-main> <CommentsDollarIcon /> {{ $t('Saldo comarketing') }} </pf-card-header-main>
          </pf-card-header>
          <pf-divider />

          <pf-card-body>
            <pf-title h="1">{{ $currency(totalComarketing, login.currency) }}</pf-title>
          </pf-card-body>
          <pf-card-footer>
            {{ $t('Commissione comarketing') }}: <strong>{{ $percent(account.comarketing_percentage / 100) }}</strong>
          </pf-card-footer>
        </pf-card>

        <pf-card :class="[gridStyles.gridItem, gridStyles.modifiers['4ColOnSm'], gridStyles.modifiers['3ColOnXl']]">
          <pf-card-header>
            <pf-card-header-main> <CashRegisterIcon /> {{ $t('Saldo cashback') }} </pf-card-header-main>
          </pf-card-header>
          <pf-divider />

          <pf-card-body>
            <pf-title h="1">{{ $currency(totalCashback, login.currency) }}</pf-title>
          </pf-card-body>
          <pf-card-footer>
            {{ $t('Commissione cashback') }}: <strong>{{ $percent(account.cashback_percentage / 100) }}</strong>
          </pf-card-footer>
        </pf-card>
      </pf-grid>
    </pf-page-section>

    <pf-page-section>
      <pf-tabs box>
        <pf-tab key="provvigioni">
          <template #title>
            <pf-tab-title-icon><WalletIcon /></pf-tab-title-icon>
            <pf-tab-title-text>{{ $t('Provvigioni') }}</pf-tab-title-text>
          </template>
          <pf-page-section no-body-wrapper>
            <collect-commissions
              :enabled="enableCollectionProvvigioni"
              :currency="login.currency"
              :total="totalProvvigioni"
              :collect-from="account.collect_from"
              :iban="iban"
              @submit="accountState.update()"
            />

            <pf-divider :class="spacingStyles.myLg" />

            <movements-list :movements="provvigioni" :currency="login.currency" />
          </pf-page-section>
        </pf-tab>

        <pf-tab key="comarketing">
          <template #title>
            <pf-tab-title-icon><CommentsDollarIcon /></pf-tab-title-icon>
            <pf-tab-title-text>{{ $t('Comarketing') }}</pf-tab-title-text>
          </template>
          <pf-page-section no-body-wrapper>
            <pf-content>
              <p>
                {{
                  $t(
                    "Ottieni benefici promuovendo: ogni volta che viene emessa una fattura a favore della tua azienda o a un cliente da te indicato, convertiamo una parte dell'importo totale in credito comarketing.",
                  )
                }}
                <br />
                {{ $t('Tale credito verrà accreditato sul tuo conto marketing non appena la fattura verrà regolarmente saldata.') }}
              </p>
              <p>
                {{
                  $t(
                    'Potenzia la tua presenza sul mercato: impiega il tuo credito marketing per avviare iniziative promozionali e valorizzare la tua attività, collaborando con il marchio Artera per la diffusione dei nostri servizi di Hosting e Cloud Premium.',
                  )
                }}
                <br />
                {{
                  $t(
                    'Presentaci un piano dettagliato del tuo progetto includendo i costi previsti, e noi ci impegneremo a coprire fino al 50% di tali spese, o fino al raggiungimento del limite massimo disponibile sul tuo conto marketing.',
                  )
                }}
              </p>
              <p>
                {{ $t('Per procedere con la realizzazione del tuo progetto, necessitiamo delle tue fatture e dei comprovanti di pagamento.') }}
                <br />
                {{
                  $t(
                    'Il tuo credito marketing verrà convertito in commissioni "standard", che potrai poi fatturarci o usare per il pagamento dei servizi da noi forniti.',
                  )
                }}
              </p>
            </pf-content>

            <pf-divider :class="spacingStyles.myLg" />

            <collect-comarketing :currency="login.currency" :total="totalComarketing" />

            <pf-divider :class="spacingStyles.myLg" />

            <movements-list :movements="comarketing" :currency="login.currency" />
          </pf-page-section>
        </pf-tab>

        <pf-tab key="cashback">
          <template #title>
            <pf-tab-title-icon><CashRegisterIcon /></pf-tab-title-icon>
            <pf-tab-title-text>{{ $t('Cashback') }}</pf-tab-title-text>
          </template>
          <pf-page-section no-body-wrapper>
            <pf-content>
              <p>{{ $t('Con il nostro programma Cashback celebrare ogni nuovo ordine è ancora più gratificante!') }}</p>
              <p>
                {{
                  $t(
                    "Al momento di un nuovo acquisto, ti offriamo la possibilità di ricevere un cashback, una parte dell'importo speso che viene accreditata per scontare i tuoi futuri acquisti.",
                  )
                }}
              </p>
              <p>
                {{
                  $t(
                    'Questo sistema è pensato per premiare la tua fiducia e il continuo supporto, offrendoti un vantaggio immediato che puoi sfruttare per ridurre i costi dei servizi futuri.',
                  )
                }}
              </p>
              <p>
                {{
                  $t(
                    'Più investi, più puoi risparmiare: il nostro programma Cashback è un modo per dire grazie e per assicurarti che ogni tuo investimento con noi non solo sia apprezzato, ma anche ricompensato.',
                  )
                }}
                <br />
                {{ $t('Unisciti a noi in questo ciclo virtuoso di crescita e risparmio.') }}
              </p>
            </pf-content>

            <section :class="spacingStyles.mtLg">
              <p v-if="!login.compiledSurvey">
                {{ $t('La generazione del credito cashback non è attiva poiché non è stato compilato il nostro questionario annuale.') }}
                <pf-button variant="link" inline small @click="showSurveyModal = true">{{ $t('Clicca qui per compilarlo') }}</pf-button
                >.
              </p>

              <i18n-t v-else-if="cashbackAttivo && account.cashback_until" keypath="La generazione del credito cashback è attiva fino al {0}" tag="p">
                <strong>{{ $date(account.cashback_until) }}</strong>
              </i18n-t>

              <p v-else-if="!cashbackAttivo && account.cashback_until">{{ $t('La generazione del credito cashback non è attualmente attiva') }}</p>

              <x-form
                v-else-if="!account.cashback_until && !account.cashback_months.length"
                style="max-width: 80ch"
                :submit-text="$t('Salva')"
                :prevent-submit="savingCashbackMonths"
                @submit="saveCashBackMonths"
              >
                <form-group :name="$t('Seleziona i 3 mesi dell\'anno durante i quali desideri che venga generato il cashback.')">
                  <pf-grid>
                    <pf-grid-item span="6" lg="2" v-for="month in 12" :key="month" class="capitalized">
                      <pf-checkbox
                        :value="month"
                        name="cashback_months[]"
                        :model-value="cashback_months.includes(month)"
                        @change="changeCashBackMonths"
                        :disabled="lockMonth(month)"
                        :label="new Date(0, month - 1).toLocaleString(undefined, { month: 'long' })"
                      /><!-- Sistemare lingua -->
                    </pf-grid-item>
                  </pf-grid>
                </form-group>
              </x-form>

              <pf-content v-else>
                <p>{{ $t('Il cashback verrà generato, secondo le tue indicazioni, nei mesi di:') }}</p>
                <pf-list>
                  <pf-list-item v-for="month in account.cashback_months.sort((a, b) => (a < b ? -1 : 1))" :key="month" class="capitalized">
                    {{ new Date(0, month - 1).toLocaleString(undefined, { month: 'long' }) }}
                  </pf-list-item>
                </pf-list>
                <i18n-t keypath="{0} per modificare la preferenza selezionata." tag="p">
                  <router-link :to="{ name: 'tickets' }">
                    {{ $t('Apri un ticket') }}
                  </router-link>
                </i18n-t>
              </pf-content>
            </section>

            <pf-divider :class="spacingStyles.myLg" />

            <movements-list ref="list" :movements="cashback" :currency="login.currency" />
          </pf-page-section>
        </pf-tab>
      </pf-tabs>
    </pf-page-section>

    <introduction-form
      v-if="showSurveyModal"
      modal
      v-model:open="showSurveyModal"
      :title="$t('Compila il questionario')"
      kind="survey"
      @submit="submittedSurvey"
    />
  </page>
</template>

<style>
.capitalized {
  text-transform: capitalize;
}
</style>

<script lang="ts" setup>
import { useAlertStore } from '@/store/alerts';
import { $t as _t } from '@/i18n';
import { type Ref, ref, computed, watch } from 'vue';
import { parseDateTime } from '@common/date';
import { DateTime } from 'luxon';
import { asyncComputed } from '@common/asyncComputed';
import CollectCommissions from '../components/collect-commissions.vue';
import CollectComarketing from '../components/collect-comarketing.vue';
import IntroductionForm from '../components/introduction-form.vue';
import MovementsList from '../components/movements-list.vue';
import { useLoginStore } from '@/store/login';
import { AccountMovementsResource, CompanyResource, type AccountMovement } from '@/resources';
import WalletIcon from '@vue-patternfly/icons/wallet-icon';
import CashRegisterIcon from '@vue-patternfly/icons/cash-register-icon';
import CommentsDollarIcon from '@vue-patternfly/icons/comments-dollar-icon';
import gridStyles from '@patternfly/react-styles/css/layouts/Grid/grid';
import spacingStyles from '@patternfly/react-styles/css/utilities/Spacing/spacing';
import { useFunctionRunningState } from '@common/use';

const login = useLoginStore();

const { result: account, state: accountState } = asyncComputed(() => new AccountMovementsResource().get());

const iban: Ref<string | null> = ref(null);
const cashback_months: Ref<number[]> = ref([]);
const showSurveyModal = ref(false);

const provvigioni = computed(() => account.value.movements.filter(m => ['provvigioni', 'segnalazioni'].includes(m.tipo)));
const totalProvvigioni = computed(() => Math.floor(totalByTipo(provvigioni.value) * 100) / 100);
const enableCollectionProvvigioni = computed(() => totalProvvigioni.value >= account.value.collect_from);
const comarketing = computed(() => account.value.movements.filter(m => m.tipo === 'comarketing'));
const totalComarketing = computed(() => Math.floor(totalByTipo(comarketing.value) * 100) / 100);
const cashback = computed(() => account.value.movements.filter(m => m.tipo === 'cashback'));
const totalCashback = computed(() => Math.floor(totalByTipo(cashback.value) * 100) / 100);
const cashbackAttivo = computed(() => account.value.cashback_until !== null && parseDateTime(account.value.cashback_until) >= DateTime.local());

watch(
  () => account.value?.iban,
  v => (iban.value = v),
);

function totalByTipo(movements: AccountMovement[]): number {
  return movements.reduce((t, i) => t + i.amount, 0);
}

function lockMonth(month: number) {
  if (cashback_months.value.includes(month)) {
    return false;
  }
  return cashback_months.value.length >= 3;
}

function submittedSurvey() {
  showSurveyModal.value = false;
  login.refresh();
}

function changeCashBackMonths(e: Event) {
  if (e.target instanceof HTMLInputElement) {
    if (e.target.checked) {
      cashback_months.value.push(Number(e.target.value));
    } else {
      cashback_months.value.splice(cashback_months.value.indexOf(Number(e.target.value)), 1);
    }
  }
}

const { isRunning: savingCashbackMonths, run: saveCashBackMonths } = useFunctionRunningState(
  async () => {
    const alert = useAlertStore();

    if (login.id_azienda === null || account.value.cashback_months.length) {
      return;
    }

    if (cashback_months.value.length !== 3) {
      alert.add({ title: _t('Seleziona 3 mesi'), type: 'warning', toast: true });
      return;
    }

    try {
      await new CompanyResource().save(login.id_azienda, { cashback_months: cashback_months.value });
      alert.add({ title: _t('Preferenza salvata.'), type: 'success', toast: true });
    } catch (e) {
      return;
    }
    account.value.cashback_months = cashback_months.value;
  },
  { preventMultiple: true },
);
</script>
